import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Fade,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import BalanceIcon from '@mui/icons-material/Balance';

const OptimizationProgress = ({ open, onClose, mode = 'optimize' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  const steps = useMemo(() => mode === 'optimize' ? [
    {
      label: 'Parsing flight data',
      delay: 50,
      icon: <SearchIcon />,
      details: 'Reading Excel file and extracting flight information'
    },
    {
      label: 'Building constraint model',
      delay: 100,
      icon: <TuneIcon />,
      details: 'Setting up belt schedules, conflicts, and hourly limits'
    },
    {
      label: 'Running OR-Tools solver',
      delay: 150,
      icon: <AutorenewIcon />,
      details: 'Finding optimal belt assignments'
    },
    {
      label: 'Balancing hourly workloads',
      delay: 200,
      icon: <BalanceIcon />,
      details: 'Minimizing imbalances across hours'
    },
    {
      label: 'Finalizing assignments',
      delay: null, // Will keep spinning until dialog closes
      icon: <CheckCircleIcon />,
      details: 'Calculating metrics and detecting conflicts'
    }
  ] : [
    {
      label: 'Applying new parameters',
      delay: 50,
      icon: <TuneIcon />,
      details: 'Updating configuration settings'
    },
    {
      label: 'Running OR-Tools solver',
      delay: 100,
      icon: <AutorenewIcon />,
      details: 'Re-optimizing with new constraints'
    },
    {
      label: 'Balancing hourly workloads',
      delay: 150,
      icon: <BalanceIcon />,
      details: 'Minimizing imbalances across hours'
    },
    {
      label: 'Finalizing assignments',
      delay: null, // Will keep spinning until dialog closes
      icon: <CheckCircleIcon />,
      details: 'Applying new assignments'
    }
  ], [mode]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setElapsedTime(0);
      return;
    }

    const timeouts = [];

    // Start elapsed time counter
    const startTime = Date.now();
    const timeInterval = setInterval(() => {
      setElapsedTime((Date.now() - startTime) / 1000);
    }, 100);

    // Set up checkmarks to appear at staggered delays (except for last step which stays spinning)
    steps.forEach((step, index) => {
      if (step.delay !== null) {
        const timeout = setTimeout(() => {
          setCurrentStep(index + 1);
        }, step.delay);
        timeouts.push(timeout);
      }
    });

    return () => {
      clearInterval(timeInterval);
      timeouts.forEach(t => clearTimeout(t));
    };
  }, [open, steps]);

  const getStepIcon = (index) => {
    if (index < currentStep) {
      // Completed - green checkmark
      return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />;
    } else if (index === currentStep) {
      // Currently running - spinner
      return <CircularProgress size={20} sx={{ color: '#F9B612' }} />;
    } else {
      // Not started yet - empty circle
      return <RadioButtonUncheckedIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 24 }} />;
    }
  };

  const formatTime = (seconds) => {
    return `${seconds.toFixed(1)}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(30, 30, 40, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }
      }}
    >
      <DialogTitle sx={{
        color: '#F9B612',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutorenewIcon sx={{ animation: 'spin 2s linear infinite' }} />
          {mode === 'optimize' ? 'Optimizing Belt Assignments' : 'Re-optimizing Assignments'}
        </Box>
        <Chip
          label={`${formatTime(elapsedTime)} elapsed`}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff'
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Steps List */}
        <List sx={{ p: 0 }}>
          {steps.map((step, index) => (
            <Fade in={true} key={index} timeout={300}>
              <ListItem
                sx={{
                  opacity: index < currentStep ? 1 : 0.6,
                  transition: 'all 0.3s',
                  bgcolor: index === currentStep - 1 ? 'rgba(249, 182, 18, 0.1)' : 'transparent',
                  borderRadius: 1,
                  mb: 1,
                  py: 1.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getStepIcon(index)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        color: index < currentStep ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                        fontWeight: index === currentStep - 1 ? 600 : 400
                      }}
                    >
                      {step.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {step.details}
                    </Typography>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>

        {/* Simple note at bottom */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Optimization typically completes in 10-20 seconds
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Inject keyframe animation once
if (!document.getElementById('optimization-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'optimization-spin-keyframes';
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default OptimizationProgress;